/**
 * Проверка идущего задания (E4.6c): каждое наблюдение пользователя сверяется с
 * сохранённым на сервере состоянием учебного экземпляра -- учебный фасад отдаёт его
 * заново, черновик формы и оптимистичное изменение не учитываются. Во время тура и паузы
 * задание не проверяется.
 */

import { useEffect } from 'react';
import { getTrainingView, type TrainingManifest, type TrainingViewKind } from '@/api/training';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { advanceExercise } from './exercises';
import { setExerciseFeedback, useExerciseStore } from './exerciseStore';
import { onTrainingObservation, type TrainingObservation } from './observations';
import { useTourStore } from './tourStore';
import { useTrainingProgress } from './useTrainingProgress';

/** Состояние объекта наблюдения на сервере: экран, предмет и параметры учебного фасада */
function confirmation(
	observation: TrainingObservation,
	manifest: TrainingManifest,
): { view: TrainingViewKind; subject: string; params: Record<string, string> } {
	switch (observation.kind) {
		case 'resource-visible': {
			const ref = Object.values(manifest.resources).find(
				(value) => value.id === observation.entity_id,
			);
			return {
				view: 'resource',
				subject: 'resource',
				params: { kind: ref?.kind ?? '', id: observation.entity_id },
			};
		}
		case 'event-opened':
			return { view: 'event', subject: 'event', params: { id: observation.entity_id } };
		case 'event-saved':
			return { view: 'event', subject: 'event_workflow', params: { id: observation.entity_id } };
		case 'policy-saved':
			return { view: 'policies', subject: 'policy', params: { id: observation.entity_id } };
		case 'llm-event-opened':
			return { view: 'llm', subject: 'llm_event', params: { id: observation.entity_id } };
	}
}

const SAVES: readonly TrainingObservation['kind'][] = ['event-saved', 'policy-saved'];

export function ExerciseRuntime() {
	const { manifest } = useWorkspaceDataSource();
	const active = useExerciseStore((state) => state.active);
	const { update } = useTrainingProgress();

	useEffect(() => {
		if (active === null || manifest === null) return undefined;
		let current = true;
		let queue = Promise.resolve();
		const off = onTrainingObservation((observation) => {
			if (
				useTourStore.getState().tour.phase !== 'idle' &&
				useTourStore.getState().tour.phase !== 'paused'
			)
				return;
			const { view, subject, params } = confirmation(observation, manifest);
			queue = queue.then(async () => {
				if (!current) return;
				return getTrainingView(manifest.instance_id, view, subject, params)
					.then((confirmed) => {
						if (!current) return;
						let advanced = false;
						update((progress) => {
							const next = advanceExercise(progress, active, observation, manifest, confirmed);
							advanced = next !== progress;
							return next;
						});
						setExerciseFeedback(!advanced && SAVES.includes(observation.kind) ? 'not_done' : null);
					})
					.catch(() => {
						if (current) setExerciseFeedback('verification_failed');
					});
			});
		});
		return () => {
			current = false;
			off();
		};
	}, [active, manifest, update]);

	return null;
}
